import React from 'react';
import { CATEGORY_NAMES } from '../../../services/agent-eval';

// Checks from plans written before scenarios carried a plain-language
// `expected`. Still shown so an older plan explains what it checks.
const checks = {
    toolsCalled: 'Uses', toolsCalledAny: 'Uses one of', noToolsCalled: 'Does not use',
    replyContains: 'A reply includes', replyContainsAny: 'A reply includes at least one of',
    replyNotContains: 'No reply includes', buttonsContain: 'Offers a button containing',
    firstButtonContains: 'First button contains', interrupts: 'Handles interruption',
    noInterrupts: 'Does not trigger interruption', memoryContains: 'Remembers',
    maxTurnSeconds: 'Each reply takes at most (seconds)',
};

export function expectationLines(expectations = {}) {
    return Object.entries(expectations || {}).map(([key, value]) => {
        if (key === 'knowledgeAnswer') return `Agrees with ${value?.sourceName || 'the knowledge source'}: "${value?.evidence || ''}"`;
        if (key === 'workflowCompleted') return value ? 'Responds on the final turn (does not verify workflow completion)' : null;
        // Legacy key from before AGENT_MEMORY was dropped; evaluated as customer memory.
        if (key === 'agentMemoryWritten') return value ? 'Writes customer memory' : 'Does not write customer memory';
        if (key === 'userMemoryWritten') return value ? 'Writes customer memory' : 'Does not write customer memory';
        if (!checks[key]) return `Unsupported check: ${key} (not evaluated)`;
        return `${checks[key]}: ${Array.isArray(value) ? value.join(', ') : String(value)}`;
    }).filter(Boolean);
}

/** What a scenario sends and what a correct agent does, in plain words. */
export default function ScenarioDetails({ scenario }) {
    const lines = expectationLines(scenario.expectations);
    return <div className="space-y-3 text-sm">
        <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Customer says</p>
            <ol className="space-y-1">{(scenario.turns || []).map((turn, i) => <li key={i} className="rounded-lg bg-gray-100 px-3 py-1.5">{turn}</li>)}</ol>
        </div>
        <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">We expect</p>
            {scenario.expected && <p>{scenario.expected}</p>}
            {lines.length > 0 && <ul className="list-disc pl-5 text-gray-600">{lines.map((line, i) => <li key={i}>{line}</li>)}</ul>}
            {!scenario.expected && !lines.length && <p className="text-gray-500">The agent replies without an error.</p>}
        </div>
    </div>;
}

/** What was covered, and anything that could not be. */
export function PlanCoverage({ plan, compact = false }) {
    if (!plan) return null;
    const rows = plan.coverage || [];
    const warnings = plan.coverageWarnings || [];
    return <div className="space-y-2 text-sm">
        {plan.generationStatus === 'generating' && <p role="status">Creating test scenarios from your agent's instructions, skills, tools, workflows and knowledge…</p>}
        {plan.generationError && <p role="alert" className="text-amber-700 dark:text-amber-400">{plan.generationError}</p>}
        {!compact && rows.length > 0 && <div className="flex flex-wrap gap-1.5">
            {rows.filter(row => row.scenarios > 0).map(row => <span key={`${row.category}:${row.id}`} title={CATEGORY_NAMES[row.category] || row.kind}
                className="rounded-full border border-gray-200 px-2.5 py-0.5 text-xs text-gray-600">
                {row.name} · {row.scenarios}
            </span>)}
        </div>}
        {warnings.length > 0 && <div role="alert" className="rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-amber-800 dark:text-amber-300">
            <p className="font-medium">Some parts of your agent could not be covered</p>
            <ul className="list-disc pl-5">{warnings.map((warning, i) => <li key={i}>{warning}</li>)}</ul>
        </div>}
    </div>;
}
